import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import PageAdvice from "../components/PageAdvice";
import {
  CheckCircle, XCircle, ArrowsClockwise, CircleNotch, Key, Heartbeat,
  Plugs, Database, ArrowsLeftRight, Clock,
} from "@phosphor-icons/react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—");

const ICONS = { aroflo_v2: Plugs, aroflo_legacy: Database, maximo: ArrowsLeftRight, mex: ArrowsLeftRight };

function StatusBadge({ c }) {
  if (c.connected) return <span className="inline-flex items-center gap-1.5 rounded-sm bg-[#059669]/10 px-3 py-1.5 text-sm font-semibold text-[#059669]"><CheckCircle size={16} weight="fill" /> Healthy</span>;
  if (c.status === "planned") return <span className="inline-flex items-center gap-1.5 rounded-sm bg-[#D97706]/10 px-3 py-1.5 text-sm font-semibold text-[#D97706]"><Clock size={16} weight="fill" /> Planned</span>;
  if (c.status === "not_configured") return <span className="inline-flex items-center gap-1.5 rounded-sm bg-neutral-100 px-3 py-1.5 text-sm font-semibold text-neutral-500"><XCircle size={16} weight="fill" /> Not configured</span>;
  return <span className="inline-flex items-center gap-1.5 rounded-sm bg-[#DC2626]/10 px-3 py-1.5 text-sm font-semibold text-[#DC2626]"><XCircle size={16} weight="fill" /> Offline</span>;
}

function Row({ label, value, mono }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-center justify-between gap-4 border-b border-neutral-100 py-2.5 last:border-0">
      <span className="text-sm font-medium text-neutral-500">{label}</span>
      <span className={`text-right text-sm font-semibold text-[#0A0A0A] ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

function UsageMeter({ usage, limits }) {
  if (!usage || !usage.known) return null;
  const limit = usage.daily_limit || (limits && limits.daily) || 0;
  const used = usage.daily_used || 0;
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const tone = usage.limited ? "#DC2626" : usage.low ? "#D97706" : "#059669";
  const resetTxt = usage.reset_at ? new Date(usage.reset_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null;
  return (
    <div className="mt-4 rounded-sm border border-neutral-200 bg-neutral-50/60 p-3" data-testid="conn-usage">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-neutral-500">API usage today</span>
        <span className="text-xs font-semibold" style={{ color: tone }}>{used.toLocaleString()} / {limit.toLocaleString()}</span>
      </div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-neutral-200">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: tone }} />
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-neutral-500">
        <span>{usage.daily_remaining?.toLocaleString()} remaining{limits?.minute ? ` · ${limits.minute}/min cap` : ""}</span>
        {usage.limited && resetTxt ? <span className="font-semibold text-[#DC2626]">Resets ~{resetTxt} ({usage.reset_in_hours}h)</span> : null}
      </div>
    </div>
  );
}

export default function Connection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/connections/status");
      setData(data);
    } catch {
      setData({ connections: [], summary: { total: 0, healthy: 0 } });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4" data-testid="connection-page">
        <div>
          <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Integrations</p>
          <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">API Connections Status</h1>
          {data && <p className="mt-1 text-sm text-neutral-500" data-testid="connections-summary">{data.summary.healthy} of {data.summary.total} connections healthy</p>}
        </div>
        <button data-testid="refresh-status-button" onClick={load}
          className="flex items-center gap-2 rounded-sm border border-neutral-300 bg-white px-4 py-2.5 text-sm font-semibold text-[#0A0A0A] transition-all duration-200 hover:bg-neutral-50">
          <ArrowsClockwise size={16} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {loading && !data ? (
        <div className="mt-10 flex h-48 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3" data-testid="connections-grid">
          {data?.connections.map((c) => {
            const Icon = ICONS[c.key] || Plugs;
            return (
              <div key={c.connection_id || c.key} className="flex flex-col border border-neutral-200 bg-white p-6 shadow-sm rounded-sm" data-testid={`conn-card-${c.connection_id || c.key}`}>
                <div className="mb-4 flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-[#0055FF]/10">
                      <Icon size={22} weight="bold" className="text-[#0055FF]" />
                    </div>
                    <div>
                      <p className="font-heading text-lg font-black tracking-tight text-[#0A0A0A]">{c.name}</p>
                      <p className="text-xs text-neutral-500">{c.auth}</p>
                    </div>
                  </div>
                  <StatusBadge c={c} />
                </div>

                <div className="flex-1">
                  <Row label="Base URL" value={c.base_url} mono />
                  <Row label="Instance" value={c.instance} />
                  <Row label="Token" value={c.token_preview} mono />
                  <Row label="Credentials" value={
                    <span className="inline-flex items-center gap-1.5">
                      <Key size={13} className={c.token_configured ? "text-[#059669]" : "text-neutral-400"} />
                      {c.token_configured ? "Configured" : "Missing"}
                    </span>
                  } />
                  <Row label="Uptime" value={c.uptime ? `${c.uptime}s` : null} mono />
                  <Row label="Health" value={c.status ? <span className="inline-flex items-center gap-1.5 capitalize"><Heartbeat size={13} className="text-[#059669]" />{c.status}</span> : null} />
                  {c.key === "aroflo_v2" && c.limits && <Row label="Daily limit" value={`${(c.limits.daily || 0).toLocaleString()} requests/day`} />}
                  {c.key === "aroflo_v2" && c.limits && <Row label="Per-minute limit" value={`${c.limits.minute || 0} requests/min`} />}
                  {c.key === "aroflo_legacy" && <Row label="API quota" value="Not published by Aroflo v1" />}
                  {c.key === "aroflo_legacy" && <Row label="Timesheets stored" value={c.timesheets_synced ? c.timesheets_synced.toLocaleString() : "0"} />}
                  {c.key === "aroflo_legacy" && <Row label="Timesheets synced" value={fmt(c.timesheets_last_synced)} />}
                  {c.error && <Row label="Error" value={<span className="text-[#DC2626]">{String(c.error).slice(0, 60)}</span>} />}
                </div>

                {c.key === "aroflo_v2" && <UsageMeter usage={c.usage} limits={c.limits} />}
                {c.note && <p className="mt-4 rounded-sm bg-[#D97706]/5 p-3 text-xs text-[#92600c]">{c.note}</p>}
              </div>
            );
          })}
        </div>
      )}

      <PageAdvice items={[
        "Live status is probed each time you open this page or click Refresh. \u201cHealthy\u201d means the API responded successfully.",
        "Aroflo V2 uses an org-wide Bearer token stored server-side; the Legacy API uses HMAC-SHA512 credentials. Neither is exposed to the browser.",
        "IBM Maximo shows \u201cPlanned\u201d \u2014 it is a dry-run/planning integration only, with no live connection or writes until Phase 2 credentials are provided.",
      ]} />
    </Layout>
  );
}
